import React, { useState, useEffect } from 'react';
import { Input, List, Spin, Empty } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import lib from '../../../assets/utils/lib';

const OrganizationAutocomplete = ({ onSelect, placeholder = "Search organizations..." }) => {
  const [organizations, setOrganizations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.length >= 2) {
        searchOrganizations(searchTerm);
      } else {
        setOrganizations([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const searchOrganizations = async (query) => {
    setLoading(true);
    try {
      const response = await lib.getOrganizationsV2({
        mode: 'mini',
        search: query,
        limit: 10
      });
      
      if (response?.data?.error === 0) {
        setOrganizations(response.data.data || []);
        setShowResults(true);
      } else {
        setOrganizations([]);
        setShowResults(false);
      }
    } catch (error) {
      console.error('Search error:', error);
      setOrganizations([]);
      setShowResults(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (org) => {
    onSelect(org);
    setSearchTerm(org.org_name);
    setShowResults(false);
    setOrganizations([]);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value === '') {
      onSelect(null);
    }
  };

  const handleBlur = () => {
    // Delay hiding results to allow for click events
    setTimeout(() => {
      setShowResults(false);
    }, 200);
  };

  return (
    <div className="organization-autocomplete" style={{ position: 'relative' }}>
      <Input
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={() => {
          if (organizations.length > 0) {
            setShowResults(true);
          }
        }}
        prefix={<SearchOutlined />}
        suffix={loading ? <Spin size="small" /> : null}
        style={{ width: '100%' }}
      />
      
      {showResults && (
        <div 
          className="autocomplete-results"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'white',
            border: '1px solid #d9d9d9',
            borderTop: 'none',
            borderRadius: '0 0 6px 6px',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          {organizations.length > 0 ? (
            <List
              size="small"
              dataSource={organizations}
              renderItem={(org) => (
                <List.Item
                  style={{
                    cursor: 'pointer',
                    padding: '8px 12px',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                  onClick={() => handleSelect(org)}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'white';
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500, color: '#262626' }}>
                      {org.org_name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      Org code: {org.org_social_no} | State: {org.org_state}
                    </div>
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <div style={{ padding: '12px', textAlign: 'center' }}>
              <Empty 
                image={Empty.PRESENTED_IMAGE_SIMPLE} 
                description="No organizations found"
                style={{ margin: 0 }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrganizationAutocomplete;

